import React, { useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { createRoot, Root } from 'react-dom/client';
import {
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_NavigationContext as NavigationContext,
  UNSAFE_RouteContext as RouteContext,
} from 'react-router-dom';

/** Every toast stays on screen for at least this long (paused while hovered or focused). */
export const TOAST_DURATION_MS = 20000;
/** Most toasts shown at once; the oldest is dropped when a new one would exceed this. */
export const MAX_VISIBLE_TOASTS = 5;

export type ToastKind = 'error' | 'success' | 'info' | 'warning';

interface RouterContexts {
  navigation: React.ContextType<typeof NavigationContext>;
  location: React.ContextType<typeof LocationContext>;
  route: React.ContextType<typeof RouteContext>;
}

interface ToastRecord {
  id: number;
  key: string;
  kind: ToastKind;
  message: React.ReactNode;
  router: RouterContexts | null;
  /** Bumped whenever a duplicate resets the timer, so the item re-renders. */
  version: number;
}

interface TimerState {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
  paused: boolean;
}

let toasts: ToastRecord[] = [];
let nextId = 1;
const timers = new Map<number, TimerState>();
const listeners = new Set<() => void>();
let host: HTMLDivElement | null = null;
let root: Root | null = null;

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot() {
  return toasts;
}

function clearTimer(id: number) {
  const timer = timers.get(id);
  if (timer?.handle) clearTimeout(timer.handle);
  timers.delete(id);
}

function startTimer(id: number, ms: number) {
  const existing = timers.get(id);
  if (existing?.handle) clearTimeout(existing.handle);
  const paused = existing?.paused ?? false;
  const timer: TimerState = { handle: null, remaining: ms, startedAt: Date.now(), paused };
  if (!paused) timer.handle = setTimeout(() => dismissToast(id), ms);
  timers.set(id, timer);
}

/** Removes a toast immediately. */
export function dismissToast(id: number) {
  clearTimer(id);
  const next = toasts.filter((toast) => toast.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

function pauseToast(id: number) {
  const timer = timers.get(id);
  if (!timer || timer.paused) return;
  if (timer.handle) clearTimeout(timer.handle);
  timer.handle = null;
  timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
  timer.paused = true;
}

function resumeToast(id: number) {
  const timer = timers.get(id);
  if (!timer || !timer.paused) return;
  timer.paused = false;
  timer.startedAt = Date.now();
  timer.handle = setTimeout(() => dismissToast(id), timer.remaining);
}

/** Flattens string/number content (including arrays and nested elements) for the dedupe key. */
export function toastText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toastText).join('');
  if (React.isValidElement(node)) return toastText((node.props as { children?: React.ReactNode }).children);
  return '';
}

function ensureViewport() {
  if (typeof document === 'undefined') return;
  if (!host) {
    host = document.createElement('div');
    host.setAttribute('data-toast-host', '');
    root = createRoot(host);
    root.render(<ToastViewport />);
  }
  if (!host.isConnected) document.body.appendChild(host);
}

function pushToast(kind: ToastKind, message: React.ReactNode, key: string, router: RouterContexts | null) {
  if (!key && toastText(message) === '') return;
  const dedupeKey = `${kind}:${key || toastText(message)}`;
  ensureViewport();
  const existing = toasts.find((toast) => toast.key === dedupeKey);
  if (existing) {
    toasts = toasts.map((toast) => toast.id === existing.id
      ? { ...toast, message, router: router ?? toast.router, version: toast.version + 1 }
      : toast);
    startTimer(existing.id, TOAST_DURATION_MS);
    emit();
    return existing.id;
  }
  const id = nextId++;
  let next = [...toasts, { id, key: dedupeKey, kind, message, router, version: 0 }];
  while (next.length > MAX_VISIBLE_TOASTS) {
    clearTimer(next[0].id);
    next = next.slice(1);
  }
  toasts = next;
  startTimer(id, TOAST_DURATION_MS);
  emit();
  return id;
}

/**
 * Imperatively shows a toast. Pass `key` when the message is not plain text so
 * duplicates can be detected. Router links inside the message are not supported
 * here; use the declarative <Toast> for content that contains <Link>.
 */
export function showToast(kind: ToastKind, message: React.ReactNode, key = ''): number | undefined {
  return pushToast(kind, message, key, null);
}

/** Test helper: clears all toasts and timers and unmounts the viewport. */
export function __resetToastsForTests() {
  timers.forEach((timer) => { if (timer.handle) clearTimeout(timer.handle); });
  timers.clear();
  toasts = [];
  nextId = 1;
  // Callers in tests should wrap this in act() (see setupTests.ts).
  root?.unmount();
  root = null;
  host?.remove();
  host = null;
  listeners.clear();
}

interface ToastProps {
  kind: ToastKind;
  children?: React.ReactNode;
  /** Explicit dedupe key; needed when the content is not plain text that identifies the message. */
  id?: string;
}

/**
 * Declarative toast: renders nothing in place. When mounted, or when its
 * message changes, it pushes a toast into the global bottom-right stack. The
 * toast lives for TOAST_DURATION_MS regardless of whether this component stays
 * mounted.
 */
export function Toast({ kind, children, id }: ToastProps) {
  const navigation = useContext(NavigationContext);
  const location = useContext(LocationContext);
  const route = useContext(RouteContext);
  const key = id || toastText(children);
  const latest = React.useRef<{ children: React.ReactNode; router: RouterContexts | null }>({ children, router: null });
  latest.current = { children, router: navigation && location ? { navigation, location, route } : null };

  useEffect(() => {
    if (!key) return;
    pushToast(kind, latest.current.children, key, latest.current.router);
  }, [kind, key]);

  return null;
}

const kindStyles: Record<ToastKind, string> = {
  error: 'border-red-200 bg-red-50 text-red-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  success: 'border-green-200 bg-green-50 text-green-800',
  info: 'border-blue-200 bg-blue-50 text-blue-900',
};

function RouterBridge({ router, children }: { router: RouterContexts | null; children: React.ReactNode }) {
  if (!router) return <>{children}</>;
  return (
    <NavigationContext.Provider value={router.navigation}>
      <LocationContext.Provider value={router.location}>
        <RouteContext.Provider value={router.route}>{children}</RouteContext.Provider>
      </LocationContext.Provider>
    </NavigationContext.Provider>
  );
}

function ToastItem({ toast }: { toast: ToastRecord }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;

  useEffect(() => {
    if (paused) pauseToast(toast.id);
    else resumeToast(toast.id);
  }, [paused, toast.id]);

  const assertive = toast.kind === 'error' || toast.kind === 'warning';
  return (
    <div
      role={assertive ? 'alert' : 'status'}
      data-toast-kind={toast.kind}
      className={`pointer-events-auto flex w-full items-start gap-3 rounded-md border p-4 text-sm shadow-lg select-text break-words ${kindStyles[toast.kind]}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}
    >
      <div data-toast-message="" className="min-w-0 flex-1"><RouterBridge router={toast.router}>{toast.message}</RouterBridge></div>
      <button
        type="button"
        aria-label="Dismiss notification"
        className="-m-1 shrink-0 rounded p-1 leading-none opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
        onClick={() => dismissToast(toast.id)}
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

function ToastViewport() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <div
      data-testid="toast-viewport"
      className="pointer-events-none fixed bottom-4 right-4 left-4 z-[60] flex flex-col items-end gap-2 sm:left-auto sm:w-96 sm:max-w-[24rem] print:hidden"
    >
      {items.map((toast) => <ToastItem key={toast.id} toast={toast} />)}
    </div>
  );
}

/** Returns the toast elements currently on screen (for tests). */
export function getToastElements(): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  return Array.from(document.querySelectorAll<HTMLElement>('[data-testid="toast-viewport"] [data-toast-kind]'));
}
